import { c as _c } from "react/compiler-runtime";
import { IcoArrowLeftSmall16, IcoFileText16, IcoPlusSmall16 } from '@onefootprint/icons';
import type { SkillFileNode } from '@onefootprint/request-types/dashboard';
import { IconButton, LinkButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import InlineEditableText from '@/components/inline-editable-text';
import { useSkillsSave } from '../../hooks/use-skills-save';
import { useBuilder } from '../../store/provider';
import { getAtPath } from '../../store/skills-path';
import { DropOverlay, useDragDropProps } from './hooks/use-drag-drop-props';
import { useDropTarget } from './hooks/use-drop-target';
import SkillActions from './skill-actions';
import SkillDocument from './skill-document';
import { SkillNode } from './skill-node';
type SkillDetailProps = {
  node: SkillFileNode;
  path: string;
};
const SkillDetail = t0 => {
  const $ = _c(81);
  const {
    node,
    path
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.skills"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    state,
    actions,
    isReadOnly
  } = useBuilder();
  const save = useSkillsSave();
  const {
    dropTarget,
    setDropTarget
  } = useDropTarget();
  const {
    onDrop
  } = useDragDropProps();
  const selectedPath = state.selectedSkillPath;
  let t2;
  if ($[1] !== selectedPath || $[2] !== state.bundle) {
    t2 = selectedPath ? getAtPath(state.bundle.skills, selectedPath) : undefined;
    $[1] = selectedPath;
    $[2] = state.bundle;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const selected = t2;
  let t3;
  if ($[4] !== actions.skills || $[5] !== isReadOnly || $[6] !== node.kind || $[7] !== path || $[8] !== setDropTarget) {
    t3 = event => {
      if (isReadOnly || node.kind !== "folder" || !event.dataTransfer.types.includes("Files")) {
        return;
      }
      event.preventDefault();
      setDropTarget({
        path,
        position: "inside",
        onDrop: nodes => actions.skills.dropExternal(path, "inside", nodes)
      });
    };
    $[4] = actions.skills;
    $[5] = isReadOnly;
    $[6] = node.kind;
    $[7] = path;
    $[8] = setDropTarget;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const onDragOver = t3;
  const t4 = isReadOnly ? undefined : onDrop;
  let t5;
  if ($[10] !== dropTarget || $[11] !== path) {
    t5 = dropTarget?.path === path && dropTarget.position === "inside" && <DropOverlay />;
    $[10] = dropTarget;
    $[11] = path;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== actions.skills) {
    t6 = () => actions.skills.select(null);
    $[13] = actions.skills;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t) {
    t7 = t(_temp);
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <LinkButton variant="label-3" iconComponent={IcoArrowLeftSmall16} iconPosition="left" onClick={t6} className="shrink-0">{t7}</LinkButton>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span aria-hidden={true} className="text-quaternary">/</span>;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = node.name;
  let t11;
  if ($[21] !== t) {
    t11 = t(_temp2);
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t) {
    t12 = t(_temp3);
    $[23] = t;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t) {
    t13 = t(_temp4);
    $[25] = t;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== save) {
    t14 = () => {
      save();
      return true;
    };
    $[27] = save;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const t15 = !isReadOnly && node.name !== "SKILL.md";
  const t16 = !node.name && !isReadOnly;
  let t17;
  if ($[29] !== actions.skills || $[30] !== path) {
    t17 = name => actions.skills.rename(path, name);
    $[29] = actions.skills;
    $[30] = path;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== node.name || $[33] !== t11 || $[34] !== t12 || $[35] !== t13 || $[36] !== t14 || $[37] !== t15 || $[38] !== t16 || $[39] !== t17) {
    t18 = <div className="min-w-0 flex-1"><InlineEditableText value={t10} fallback={t11} placeholder={t12} ariaLabel={t13} shouldCloseOnBlur={t14} canEdit={t15} autoFocus={t16} onChange={t17} buttonClassName="max-w-full px-0" textClassName="truncate text-label-2" /></div>;
    $[32] = node.name;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t18 || $[42] !== t8) {
    t19 = <div className="flex min-w-0 flex-1 items-center gap-3">{t8}{t9}{t18}</div>;
    $[41] = t18;
    $[42] = t8;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== isReadOnly || $[45] !== t) {
    t20 = isReadOnly && <span className="text-caption-1 text-tertiary">{t(_temp5)}</span>;
    $[44] = isReadOnly;
    $[45] = t;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== node || $[48] !== path) {
    t21 = <SkillActions node={node} path={path} />;
    $[47] = node;
    $[48] = path;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t20 || $[51] !== t21) {
    t22 = <div className="flex shrink-0 items-center gap-2">{t20}{t21}</div>;
    $[50] = t20;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t19 || $[54] !== t22) {
    t23 = <div className="flex min-h-14 shrink-0 items-center justify-between gap-4 border-b border-tertiary px-5 py-3">{t19}{t22}</div>;
    $[53] = t19;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  const t24 = node.kind === "folder";
  let t25;
  if ($[56] !== t24) {
    t25 = cx("grid min-h-0 flex-1 grid-cols-1", {
      "grid-rows-[auto_minmax(0,1fr)] @min-[480px]:grid-cols-[192px_minmax(0,1fr)] @min-[480px]:grid-rows-1 @min-[800px]:grid-cols-[260px_minmax(0,1fr)]": t24
    });
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== actions.skills || $[59] !== isReadOnly || $[60] !== node.children || $[61] !== node.kind || $[62] !== path || $[63] !== t) {
    t26 = node.kind === "folder" && <aside className="flex min-h-0 min-w-0 flex-col border-b border-tertiary bg-canvas @min-[480px]:border-r @min-[480px]:border-b-0" aria-label={t(_temp6)}><div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-tertiary px-4"><h3 className="text-label-3 text-tertiary">{t(_temp7)}</h3>{!isReadOnly && <IconButton icon={IcoPlusSmall16} variant="ghost" size="compact" aria-label={t(_temp8)} onClick={() => actions.skills.addFile(path)} />}</div><div className="max-h-40 min-h-0 flex-1 overflow-y-auto p-2 @min-[480px]:max-h-none">{node.children.map((child, index) => {
          const childPath = `${path}.${index}`;
          return <SkillNode key={childPath} node={child} path={childPath} depth={0} />;
        })}</div>{!isReadOnly && <p className="hidden shrink-0 border-t border-tertiary px-4 py-4 text-caption-1 text-tertiary @min-[480px]:block">{t(_temp9)}</p>}</aside>;
    $[58] = actions.skills;
    $[59] = isReadOnly;
    $[60] = node.children;
    $[61] = node.kind;
    $[62] = path;
    $[63] = t;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== node.children || $[66] !== node.kind || $[67] !== selected || $[68] !== selectedPath || $[69] !== t) {
    t27 = selected?.kind === "file" && selectedPath ? <SkillDocument key={selectedPath} node={selected} path={selectedPath} /> : <div className="min-h-0 min-w-0 overflow-y-auto overscroll-contain"><div className="mx-auto w-full max-w-[800px] px-6 py-8 @min-[800px]:px-12 @min-[800px]:py-10">{node.kind === "folder" && node.children.length > 0 ? <p className="text-body-2 text-tertiary">{t(_temp10)}</p> : <div className="flex min-h-80 flex-col items-start justify-center gap-4"><IcoFileText16 className="text-tertiary" /><div className="space-y-2"><h2 className="text-heading-2 text-primary">{t(_temp11)}</h2><p className="text-body-2 text-tertiary">{t(_temp12)}</p></div></div>}</div></div>;
    $[65] = node.children;
    $[66] = node.kind;
    $[67] = selected;
    $[68] = selectedPath;
    $[69] = t;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] !== t25 || $[72] !== t26 || $[73] !== t27) {
    t28 = <div className={t25}>{t26}{t27}</div>;
    $[71] = t25;
    $[72] = t26;
    $[73] = t27;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  let t29;
  if ($[75] !== onDragOver || $[76] !== t23 || $[77] !== t28 || $[78] !== t4 || $[79] !== t5) {
    t29 = <div className="@container relative flex min-h-0 min-w-0 flex-1 flex-col" onDragOver={onDragOver} onDrop={t4}>{t5}{t23}{t28}</div>;
    $[75] = onDragOver;
    $[76] = t23;
    $[77] = t28;
    $[78] = t4;
    $[79] = t5;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  return t29;
};
export default SkillDetail;
function _temp($) {
  return $.back;
}
function _temp2($_0) {
  return $_0.untitled;
}
function _temp3($_1) {
  return $_1.titleField.placeholder;
}
function _temp4($_2) {
  return $_2.titleField.label;
}
function _temp5($_3) {
  return $_3.readOnly;
}
function _temp6($_4) {
  return $_4.files;
}
function _temp7($_5) {
  return $_5.files;
}
function _temp8($_6) {
  return $_6.addFile;
}
function _temp9($_7) {
  return $_7.dropHint;
}
function _temp10($_8) {
  return $_8.noFileSelected;
}
function _temp11($_9) {
  return $_9.noFiles;
}
function _temp12($_10) {
  return $_10.noFilesDescription;
}